"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "@/app/components/ConfirmDialog";
import { useMapFeatureTools } from "./MapFeatureTools";

const MAP_W = 2400;
const MAP_H = 1600;

type PaintField =
  | "terrain"
  | "vegetation"
  | "climate"
  | "special"
  | "danger_level"
  | "nation_ref"
  | "state_ref";

interface PaletteItem {
  value: string | number;
  label: string;
  color: string;
}

interface LayersResponse {
  count: number;
  ids: string[];
  palette: Record<PaintField, PaletteItem[]>;
  values: {
    climate: number[];
    terrain: number[];
    vegetation: number[];
    special: number[];
    danger: number[];
    nation: number[];
    state: number[];
    land: number[];
    coastal: number[];
    timezone: number[];
  };
  timezones: string[];
}

const FIELD_LABEL: Record<PaintField, string> = {
  climate: "気候帯",
  terrain: "地形",
  vegetation: "植生",
  special: "特殊",
  danger_level: "危険度",
  nation_ref: "国",
  state_ref: "州",
};

const FIELDS: PaintField[] = [
  "climate",
  "terrain",
  "vegetation",
  "special",
  "danger_level",
  "nation_ref",
  "state_ref",
];

/** 陸にしか置けない項目。国は海にも置ける */
const LAND_ONLY: PaintField[] = ["terrain", "vegetation"];

const SEA: [number, number, number] = [29, 58, 92];
const UNSET_LAND: [number, number, number] = [64, 62, 58];
/** 州の境界線の色 */
const STATE_LINE: [number, number, number] = [255, 216, 96];
/** 時間帯の境界線の色 */
const TZ_LINE: [number, number, number] = [96, 216, 255];

const LABEL_STYLE: React.CSSProperties = { fontSize: 12, color: "var(--text-dim)", margin: "14px 0 6px" };

/** サイドバーのボタンの見た目。on は選んでいる状態 */
function buttonStyle(on: boolean): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    padding: "6px 8px",
    fontSize: 13,
    textAlign: "left",
    background: on ? "rgba(200,162,74,0.14)" : "var(--bg-field)",
    color: on ? "var(--accent-strong)" : "var(--text)",
    border: `1px solid ${on ? "var(--accent)" : "var(--line)"}`,
    borderRadius: 6,
    cursor: "pointer",
  };
}

/** 表示の状態。1 つにまとめて 1 回の更新で済ませる */
interface View {
  zoom: number;
  panX: number;
  panY: number;
}

export default function ProvincePaintEditor({ onBack }: { onBack: () => void }) {
  // 地図の色面。原寸で持ち、表示のときに引き伸ばす
  const faceRef = useRef<HTMLCanvasElement | null>(null);
  const faceImageRef = useRef<ImageData | null>(null);
  // 画面に出す 3 枚。下が色面、中が境界線、上が地物
  const viewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lineCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<HTMLDivElement | null>(null);

  const rasterRef = useRef<Uint16Array | null>(null);
  const idsRef = useRef<string[]>([]);
  const landRef = useRef<Uint8Array | null>(null);
  const coastalRef = useRef<Uint8Array | null>(null);
  /** 時間帯の番号。0 は未設定 */
  const timezoneRef = useRef<Int16Array | null>(null);
  const timezoneNamesRef = useRef<string[]>([]);
  const valuesRef = useRef<Record<PaintField, Int16Array> | null>(null);
  const dirtyRef = useRef<Map<PaintField, Map<string, string | number | null>>>(new Map());

  const [status, setStatus] = useState("読み込み中");
  const [ask, confirmDialog] = useConfirm();
  const [ready, setReady] = useState(false);
  const [palette, setPalette] = useState<Record<PaintField, PaletteItem[]> | null>(null);

  /** 塗るか、地物を触るか */
  const [mode, setMode] = useState<"paint" | "feature">("paint");
  const [field, setField] = useState<PaintField>("climate");
  const [brushValue, setBrushValue] = useState<string | number | null>(null);
  const [brushSize, setBrushSize] = useState(3);
  const [eraser, setEraser] = useState(false);
  const [showLine, setShowLine] = useState(true);
  /** 州の境界線を太く明るく重ねる */
  const [showStateLine, setShowStateLine] = useState(false);
  /** 時間帯の境界線を重ねる */
  const [showTimezoneLine, setShowTimezoneLine] = useState(false);
  /** 海のマスへ塗ってよいか。切ると海を守る */
  const [allowSea, setAllowSea] = useState(false);
  const [dirtyCount, setDirtyCount] = useState(0);
  const [filter, setFilter] = useState("");
  // 州の md を開いているときだけ値が入る
  const [docSlug, setDocSlug] = useState<string | null>(null);
  const [docText, setDocText] = useState("");
  const [docStatus, setDocStatus] = useState("");
  const [hover, setHover] = useState<{
    id: string;
    land: boolean;
    coastal: boolean;
    nation: string | null;
    state: string | null;
    /** 近くに在る地物の名 */
    features: string;
  } | null>(null);

  const [view, setView] = useState<View>({ zoom: 0.4, panX: 0, panY: 0 });
  const viewStateRef = useRef<View>(view);
  viewStateRef.current = view;

  const sizeRef = useRef({ w: 0, h: 0 });
  const panningRef = useRef<{ panX: number; panY: number; px: number; py: number } | null>(null);
  const paintingRef = useRef(false);
  const touchedRef = useRef<Set<number>>(new Set());
  const frameRef = useRef(0);
  /** 地物の重ね描き。useMapFeatureTools が作ったものを後で差す */
  const featureDrawRef = useRef<() => void>(() => {});
  const featureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // ── 読み込み ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatus("形と現在値を読み込み中");
        const [rasterRes, layersRes] = await Promise.all([
          fetch("/provincemap/province_raster.bin"),
          fetch("/api/province-map/layers"),
        ]);
        if (!rasterRes.ok) throw new Error("形を読めない");
        if (!layersRes.ok) throw new Error("現在値を読めない");

        const buf = await rasterRes.arrayBuffer();
        const layers = (await layersRes.json()) as LayersResponse;
        if (cancelled) return;

        rasterRef.current = new Uint16Array(buf);
        idsRef.current = layers.ids;

        const n = layers.ids.length;
        const land = new Uint8Array(n);
        const coastal = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
          land[i] = layers.values.land[i] ?? 0;
          coastal[i] = layers.values.coastal?.[i] ?? 0;
        }
        landRef.current = land;
        coastalRef.current = coastal;

        const timezone = new Int16Array(n);
        for (let i = 0; i < n; i++) timezone[i] = layers.values.timezone?.[i] ?? 0;
        timezoneRef.current = timezone;
        timezoneNamesRef.current = layers.timezones ?? [];

        const toInt16 = (arr: number[]) => {
          const out = new Int16Array(n);
          for (let i = 0; i < n; i++) out[i] = arr[i] ?? 0;
          return out;
        };
        valuesRef.current = {
          climate: toInt16(layers.values.climate),
          terrain: toInt16(layers.values.terrain),
          vegetation: toInt16(layers.values.vegetation),
          special: toInt16(layers.values.special ?? []),
          danger_level: toInt16(layers.values.danger),
          nation_ref: toInt16(layers.values.nation ?? []),
          state_ref: toInt16(layers.values.state ?? []),
        };

        const face = document.createElement("canvas");
        face.width = MAP_W;
        face.height = MAP_H;
        faceRef.current = face;
        faceImageRef.current = new ImageData(MAP_W, MAP_H);

        setPalette(layers.palette);
        setBrushValue(layers.palette.climate[0]?.value ?? null);
        setReady(true);
        setStatus(`${n.toLocaleString()} プロヴィンス`);
      } catch (e) {
        if (!cancelled) setStatus(`失敗: ${(e as Error).message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rgbTable = useMemo(() => {
    if (!palette) return null;
    const hexToRgb = (hex: string): [number, number, number] => {
      const h = hex.replace("#", "");
      return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
      ];
    };
    const out = {} as Record<PaintField, Array<[number, number, number] | null>>;
    for (const f of FIELDS) out[f] = [null, ...palette[f].map((p) => hexToRgb(p.color))];
    return out;
  }, [palette]);

  const colorOf = useCallback(
    (idx: number, f: PaintField): [number, number, number] => {
      const values = valuesRef.current!;
      const land = landRef.current!;
      const paint = values[f][idx];
      const c = rgbTable ? rgbTable[f][paint] : null;
      if (paint > 0 && c) return c;
      // 国・州・特殊を見ているときの未設定は、陸と海で少しだけ分ける
      if (f === "nation_ref" || f === "state_ref" || f === "special") {
        return land[idx] === 1 ? [44, 44, 48] : [20, 34, 50];
      }
      return land[idx] === 1 ? UNSET_LAND : SEA;
    },
    [rgbTable]
  );

  // ── 色面を作る。境界は焼き込まない ──
  const buildFace = useCallback(() => {
    const raster = rasterRef.current;
    const image = faceImageRef.current;
    const face = faceRef.current;
    if (!raster || !image || !face || !valuesRef.current || !landRef.current || !rgbTable) return;
    const data = image.data;
    const cache = new Map<number, [number, number, number]>();

    for (let p = 0; p < raster.length; p++) {
      const v = raster[p];
      const o = p * 4;
      data[o + 3] = 255;
      if (v === 0) {
        data[o] = 0;
        data[o + 1] = 0;
        data[o + 2] = 0;
        continue;
      }
      let rgb = cache.get(v);
      if (!rgb) {
        rgb = colorOf(v - 1, field);
        cache.set(v, rgb);
      }
      data[o] = rgb[0];
      data[o + 1] = rgb[1];
      data[o + 2] = rgb[2];
    }
    face.getContext("2d")!.putImageData(image, 0, 0);
  }, [colorOf, field, rgbTable]);

  /** 塗ったマスだけ色面を直す */
  const patchFace = useCallback(
    (numbers: Set<number>) => {
      const raster = rasterRef.current;
      const image = faceImageRef.current;
      const face = faceRef.current;
      if (!raster || !image || !face) return;
      const data = image.data;
      const cache = new Map<number, [number, number, number]>();
      for (let p = 0; p < raster.length; p++) {
        const v = raster[p];
        if (v === 0 || !numbers.has(v)) continue;
        let rgb = cache.get(v);
        if (!rgb) {
          rgb = colorOf(v - 1, field);
          cache.set(v, rgb);
        }
        const o = p * 4;
        data[o] = rgb[0];
        data[o + 1] = rgb[1];
        data[o + 2] = rgb[2];
      }
      face.getContext("2d")!.putImageData(image, 0, 0);
    },
    [colorOf, field]
  );

  // ── 画面へ描く ──
  // 色面は引き伸ばして転送し、境界線は画面の 1 ドットとして毎回引き直す
  const render = useCallback(() => {
    const viewCanvas = viewCanvasRef.current;
    const lineCanvas = lineCanvasRef.current;
    const face = faceRef.current;
    const raster = rasterRef.current;
    if (!viewCanvas || !lineCanvas || !face || !raster) return;

    const { w, h } = sizeRef.current;
    if (w === 0 || h === 0) return;
    const { zoom, panX, panY } = viewStateRef.current;

    // 色面
    const ctx = viewCanvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, w, h);
    const drawW = MAP_W * zoom;
    const drawH = MAP_H * zoom;
    const left = w / 2 + panX - drawW / 2;
    const top = h / 2 + panY - drawH / 2;
    ctx.drawImage(face, left, top, drawW, drawH);

    // 境界線
    const lctx = lineCanvas.getContext("2d")!;
    lctx.clearRect(0, 0, w, h);
    const stateArr = valuesRef.current?.state_ref ?? null;
    const drawState = showStateLine && !!stateArr;
    const tzArr = timezoneRef.current;
    const drawTz = showTimezoneLine && !!tzArr;
    if (!showLine && !drawState && !drawTz) return;

    const img = lctx.createImageData(w, h);
    const data = img.data;
    const inv = 1 / zoom;
    // 拡大しているときは州の線を 2 ドットにして目立たせる
    const thick = zoom >= 0.8;

    const put = (sx: number, sy2: number, rgb: [number, number, number], a: number) => {
      if (sx < 0 || sx >= w || sy2 < 0 || sy2 >= h) return;
      const o = (sy2 * w + sx) * 4;
      data[o] = rgb[0];
      data[o + 1] = rgb[1];
      data[o + 2] = rgb[2];
      data[o + 3] = a;
    };

    // 画面の各行について、ラスターの行を 1 本引いて右隣と下隣を見る
    const rowIds = new Int32Array(w);
    const prevIds = new Int32Array(w);
    const rowStates = new Int32Array(w);
    const prevStates = new Int32Array(w);
    const rowTz = new Int32Array(w);
    const prevTz = new Int32Array(w);
    let hasPrev = false;

    for (let sy = 0; sy < h; sy++) {
      const my = Math.floor((sy - top) * inv);
      if (my < 0 || my >= MAP_H) {
        hasPrev = false;
        continue;
      }
      const base = my * MAP_W;
      for (let sx = 0; sx < w; sx++) {
        const mx = Math.floor((sx - left) * inv);
        const v = mx < 0 || mx >= MAP_W ? -1 : raster[base + mx];
        rowIds[sx] = v;
        rowStates[sx] = drawState && v > 0 ? stateArr![v - 1] : -1;
        rowTz[sx] = drawTz && v > 0 ? tzArr![v - 1] : -1;
      }
      // 時間帯の境界を先に引く。州の線が上に来る
      if (drawTz) {
        for (let sx = 0; sx < w; sx++) {
          const t = rowTz[sx];
          if (t < 0) continue;
          const rightDiff = sx + 1 < w && rowTz[sx + 1] >= 0 && rowTz[sx + 1] !== t;
          const downDiff = hasPrev && prevTz[sx] >= 0 && prevTz[sx] !== t;
          if (!rightDiff && !downDiff) continue;
          put(sx, sy, TZ_LINE, 255);
          if (thick) {
            if (rightDiff) put(sx + 1, sy, TZ_LINE, 255);
            if (downDiff) put(sx, sy - 1, TZ_LINE, 255);
          }
        }
      }
      if (showLine) {
        for (let sx = 0; sx < w; sx++) {
          const v = rowIds[sx];
          if (v <= 0) continue;
          const rightDiff = sx + 1 < w && rowIds[sx + 1] > 0 && rowIds[sx + 1] !== v;
          const downDiff = hasPrev && prevIds[sx] > 0 && prevIds[sx] !== v;
          if (!rightDiff && !downDiff) continue;
          put(sx, sy, [10, 10, 12], 235);
        }
      }
      // 州の境界は後から重ねる。プロヴィンスの線より上に出す
      if (drawState) {
        for (let sx = 0; sx < w; sx++) {
          const s = rowStates[sx];
          if (s < 0) continue;
          const rightDiff = sx + 1 < w && rowStates[sx + 1] >= 0 && rowStates[sx + 1] !== s;
          const downDiff = hasPrev && prevStates[sx] >= 0 && prevStates[sx] !== s;
          if (!rightDiff && !downDiff) continue;
          put(sx, sy, STATE_LINE, 255);
          if (thick) {
            if (rightDiff) put(sx + 1, sy, STATE_LINE, 255);
            if (downDiff) put(sx, sy - 1, STATE_LINE, 255);
          }
        }
      }
      prevIds.set(rowIds);
      prevStates.set(rowStates);
      prevTz.set(rowTz);
      hasPrev = true;
    }
    lctx.putImageData(img, 0, 0);
  }, [showLine, showStateLine, showTimezoneLine]);

  const requestRender = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      render();
      featureDrawRef.current();
    });
  }, [render]);

  // 表示領域の大きさを追う
  useEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    const apply = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      sizeRef.current = { w, h };
      for (const c of [viewCanvasRef.current, lineCanvasRef.current, featureCanvasRef.current]) {
        if (c) {
          c.width = w;
          c.height = h;
        }
      }
      requestRender();
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [requestRender]);

  useEffect(() => {
    if (!ready) return;
    buildFace();
    requestRender();
  }, [ready, buildFace, requestRender]);

  useEffect(() => {
    requestRender();
  }, [view, showLine, showStateLine, showTimezoneLine, requestRender]);

  // ── 座標 ──
  const toMapXY = useCallback((clientX: number, clientY: number) => {
    const el = viewRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const { w, h } = sizeRef.current;
    const { zoom, panX, panY } = viewStateRef.current;
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const left = w / 2 + panX - (MAP_W * zoom) / 2;
    const top = h / 2 + panY - (MAP_H * zoom) / 2;
    const x = Math.floor((sx - left) / zoom);
    const y = Math.floor((sy - top) / zoom);
    if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) return null;
    return { x, y };
  }, []);

  // ── 地物 ──
  const features = useMapFeatureTools({
    ready,
    active: mode === "feature",
    viewStateRef,
    sizeRef,
    setView,
    toMapXY,
    setStatus,
    ask,
    btn: buttonStyle,
    label: LABEL_STYLE,
  });
  featureDrawRef.current = features.draw;

  // ── 塗る ──
  const paintAt = useCallback(
    (clientX: number, clientY: number) => {
      const raster = rasterRef.current;
      const values = valuesRef.current;
      const ids = idsRef.current;
      if (!raster || !values || !palette) return;
      const pos = toMapXY(clientX, clientY);
      if (!pos) return;

      // 筆の太さは画面の見かけに合わせる
      const zoom = viewStateRef.current.zoom;
      const r = Math.max(0, Math.round((brushSize - 1) / Math.max(zoom, 0.12)));

      const land = landRef.current;
      // 項目そのものが陸だけのものか、海の編集を切っているときは海を外す
      const skipSea = LAND_ONLY.includes(field) || !allowSea;

      const targets = new Set<number>();
      let seaRejected = 0;
      for (let dy = -r; dy <= r; dy++) {
        const y = pos.y + dy;
        if (y < 0 || y >= MAP_H) continue;
        const row = y * MAP_W;
        for (let dx = -r; dx <= r; dx++) {
          const x = pos.x + dx;
          if (x < 0 || x >= MAP_W) continue;
          const v = raster[row + x];
          if (v <= 0) continue;
          if (skipSea && land && land[v - 1] !== 1) {
            seaRejected++;
            continue;
          }
          targets.add(v);
        }
      }
      if (targets.size === 0) {
        if (seaRejected > 0) {
          setStatus(
            LAND_ONLY.includes(field)
              ? `${FIELD_LABEL[field]}は陸にしか置けない`
              : "海の編集が切れている"
          );
        }
        return;
      }

      const list = palette[field];
      const valueIndex = eraser ? 0 : list.findIndex((p) => p.value === brushValue) + 1;
      const stored: string | number | null = eraser
        ? field === "danger_level"
          ? 1
          : null
        : (brushValue as string | number);
      const nextIndex = eraser && field === "danger_level" ? 1 : valueIndex;

      const dirty = dirtyRef.current;
      let fieldDirty = dirty.get(field);
      if (!fieldDirty) {
        fieldDirty = new Map();
        dirty.set(field, fieldDirty);
      }

      const changed = new Set<number>();
      for (const v of targets) {
        if (touchedRef.current.has(v)) continue;
        touchedRef.current.add(v);
        const idx = v - 1;
        if (values[field][idx] === nextIndex) continue;
        values[field][idx] = nextIndex;
        fieldDirty.set(ids[idx], stored);
        changed.add(v);
      }
      if (changed.size > 0) {
        patchFace(changed);
        requestRender();
        let total = 0;
        dirty.forEach((m) => (total += m.size));
        setDirtyCount(total);
      }
    },
    [allowSea, brushSize, brushValue, eraser, field, palette, patchFace, requestRender, toMapXY]
  );

  // ── 操作 ──
  const onPointerDown = (e: React.PointerEvent) => {
    if (!ready) return;
    // 地図の上で押したときだけ受ける。重ねた画面の操作は拾わない
    if (e.target !== viewCanvasRef.current && e.target !== lineCanvasRef.current) return;
    if (e.button === 1 || e.button === 2 || e.shiftKey) {
      const v = viewStateRef.current;
      panningRef.current = { panX: v.panX, panY: v.panY, px: e.clientX, py: e.clientY };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    if (mode === "feature") {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      features.onPointerDown(e);
      return;
    }
    paintingRef.current = true;
    touchedRef.current.clear();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    paintAt(e.clientX, e.clientY);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = panningRef.current;
    if (p) {
      setView((v) => ({ ...v, panX: p.panX + (e.clientX - p.px), panY: p.panY + (e.clientY - p.py) }));
      return;
    }
    if (paintingRef.current) {
      paintAt(e.clientX, e.clientY);
      return;
    }
    if (mode === "feature" && features.onPointerMove(e)) return;
    const raster = rasterRef.current;
    const pos = toMapXY(e.clientX, e.clientY);
    if (raster && pos) {
      const v = raster[pos.y * MAP_W + pos.x];
      if (v > 0) {
        const land = landRef.current;
        const coastal = coastalRef.current;
        const values = valuesRef.current;
        const nationIdx = values ? values.nation_ref[v - 1] : 0;
        const stateIdx = values ? values.state_ref[v - 1] : 0;
        const nationName =
          nationIdx > 0 ? (palette?.nation_ref?.[nationIdx - 1]?.label ?? null) : null;
        const stateName =
          stateIdx > 0 ? (palette?.state_ref?.[stateIdx - 1]?.label ?? null) : null;
        setHover({
          id: idsRef.current[v - 1],
          land: land ? land[v - 1] === 1 : false,
          coastal: coastal ? coastal[v - 1] === 1 : false,
          nation: nationName,
          state: stateName,
          features: features.namesNear(pos.x, pos.y),
        });
        return;
      }
    }
    setHover(null);
  };

  const endPointer = (e: React.PointerEvent) => {
    if (mode === "feature") features.onPointerUp(e);
    paintingRef.current = false;
    panningRef.current = null;
    touchedRef.current.clear();
  };

  /** カーソルの下の点を動かさずに拡大縮小する */
  const zoomAt = useCallback((factor: number, clientX: number, clientY: number) => {
    const el = viewRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = clientX - rect.left - sizeRef.current.w / 2;
    const cy = clientY - rect.top - sizeRef.current.h / 2;
    setView((v) => {
      const next = Math.min(16, Math.max(0.12, v.zoom * factor));
      const k = next / v.zoom;
      return {
        zoom: next,
        panX: cx - (cx - v.panX) * k,
        panY: cy - (cy - v.panY) * k,
      };
    });
  }, []);

  const onWheel = (e: React.WheelEvent) => {
    zoomAt(e.deltaY < 0 ? 1.25 : 1 / 1.25, e.clientX, e.clientY);
  };

  const zoomCenter = (factor: number) => {
    const el = viewRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    zoomAt(factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  const fitWhole = () => {
    const { w, h } = sizeRef.current;
    const z = Math.min(w / MAP_W, h / MAP_H);
    setView({ zoom: Math.max(0.12, z), panX: 0, panY: 0 });
  };

  // ── 保存 ──
  const save = async () => {
    const dirty = dirtyRef.current;
    if (dirty.size === 0) return;
    setStatus("保存中");
    try {
      for (const [f, map] of dirty) {
        const byValue = new Map<string, string[]>();
        for (const [id, value] of map) {
          const key = JSON.stringify(value);
          const arr = byValue.get(key);
          if (arr) arr.push(id);
          else byValue.set(key, [id]);
        }
        for (const [key, ids] of byValue) {
          const value = JSON.parse(key) as string | number | null;
          const res = await fetch("/api/province-map/paint", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ field: f, value, ids }),
          });
          const json = await res.json();
          if (!res.ok || !json.ok) throw new Error(json.error ?? `${f} の保存に失敗`);
        }
      }
      dirtyRef.current = new Map();
      setDirtyCount(0);
      setStatus("保存した");
    } catch (e) {
      setStatus(`保存に失敗: ${(e as Error).message}`);
    }
  };

  /** master を読み直して画面へ取り込む */
  const reload = async () => {
    if (dirtyCount > 0 && !(await ask("保存していない塗りがある。捨てて読み直す？", { ok: "捨てて読み直す", danger: true }))) return;
    if (
      features.hasUnsaved &&
      !(await ask("保存していない地物の形がある。捨てて読み直す？", { ok: "捨てて読み直す", danger: true }))
    )
      return;
    features.discardDraft();
    setStatus("読み直し中");
    try {
      await fetch("/api/province-map/reload", { method: "POST" });
      await features.reload();
      const res = await fetch("/api/province-map/layers");
      if (!res.ok) throw new Error("現在値を読めない");
      const layers = (await res.json()) as LayersResponse;

      const n = layers.ids.length;
      idsRef.current = layers.ids;
      const land = new Uint8Array(n);
      const coastal = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        land[i] = layers.values.land[i] ?? 0;
        coastal[i] = layers.values.coastal?.[i] ?? 0;
      }
      landRef.current = land;
      coastalRef.current = coastal;

      const toInt16 = (arr: number[]) => {
        const out = new Int16Array(n);
        for (let i = 0; i < n; i++) out[i] = arr[i] ?? 0;
        return out;
      };
      valuesRef.current = {
        climate: toInt16(layers.values.climate),
        terrain: toInt16(layers.values.terrain),
        vegetation: toInt16(layers.values.vegetation),
        special: toInt16(layers.values.special ?? []),
        danger_level: toInt16(layers.values.danger),
        nation_ref: toInt16(layers.values.nation ?? []),
        state_ref: toInt16(layers.values.state ?? []),
      };

      dirtyRef.current = new Map();
      setDirtyCount(0);
      setPalette(layers.palette);
      buildFace();
      requestRender();
      setStatus(`読み直した。${n.toLocaleString()} プロヴィンス`);
    } catch (e) {
      setStatus(`読み直しに失敗: ${(e as Error).message}`);
    }
  };

  const fullList = palette?.[field] ?? [];
  const currentList = filter.trim()
    ? fullList.filter(
        (p) =>
          p.label.includes(filter.trim()) ||
          String(p.value).includes(filter.trim().toLowerCase())
      )
    : fullList;

  /** 選んでいる州の md を開く */
  const openDoc = async (slug: string) => {
    setDocSlug(slug);
    setDocText("");
    setDocStatus("読み込み中");
    try {
      const res = await fetch(`/api/province-map/state-doc?slug=${encodeURIComponent(slug)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "読めない");
      setDocText(json.text);
      setDocStatus("");
    } catch (e) {
      setDocStatus(`失敗: ${(e as Error).message}`);
    }
  };

  const saveDoc = async () => {
    if (!docSlug) return;
    setDocStatus("保存中");
    try {
      const res = await fetch("/api/province-map/state-doc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: docSlug, text: docText }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error ?? "保存できない");
      setDocStatus(`保存した（${json.label}）`);
      // 一覧の名前を入れ替える
      setPalette((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        next.state_ref = (prev.state_ref ?? []).map((x) =>
          x.value === docSlug ? { ...x, label: json.label } : x
        );
        return next;
      });
    } catch (e) {
      setDocStatus(`失敗: ${(e as Error).message}`);
    }
  };

  const btn = buttonStyle;
  const label = LABEL_STYLE;
  const layer: React.CSSProperties = { position: "absolute", left: 0, top: 0 };

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", background: "var(--bg)", color: "var(--text)" }}>
      {confirmDialog}
      <aside
        style={{
          width: 230,
          flexShrink: 0,
          borderRight: "1px solid var(--line)",
          background: "var(--bg-panel)",
          padding: "12px 12px calc(12px + env(safe-area-inset-bottom))",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong style={{ color: "var(--accent)", letterSpacing: "0.1em" }}>MAP 編集</strong>
          <button onClick={onBack} style={{ ...btn(false), width: "auto", padding: "4px 10px" }}>
            戻る
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 10 }}>
          <button style={{ ...btn(mode === "paint"), textAlign: "center" }} onClick={() => setMode("paint")}>
            塗る
          </button>
          <button
            style={{ ...btn(mode === "feature"), textAlign: "center" }}
            onClick={() => {
              setMode("feature");
              setDocSlug(null);
            }}
          >
            地物
          </button>
        </div>

        {mode === "feature" && features.panel}

        {mode === "paint" && (
          <>
        <div style={label}>塗る項目</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
          {FIELDS.map((f) => (
            <button
              key={f}
              style={{ ...btn(field === f), textAlign: "center" }}
              onClick={() => {
                setField(f);
                setEraser(false);
                setFilter("");
                setBrushValue(palette?.[f]?.[0]?.value ?? null);
              }}
            >
              {FIELD_LABEL[f]}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 10 }}>
          <button
            style={btn(allowSea)}
            onClick={() => setAllowSea((v) => !v)}
            disabled={LAND_ONLY.includes(field)}
            title={
              LAND_ONLY.includes(field)
                ? `${FIELD_LABEL[field]}は陸にしか置けない`
                : "海のマスを塗るかどうか"
            }
          >
            海を塗る {LAND_ONLY.includes(field) ? "不可" : allowSea ? "入" : "切"}
          </button>
        </div>

        {LAND_ONLY.includes(field) && (
          <p style={{ fontSize: 11, color: "var(--text-dim)", margin: "8px 0 0" }}>
            陸のマスだけに置ける
          </p>
        )}

        <div style={label}>
          色{fullList.length > 12 ? `（${currentList.length} / ${fullList.length}）` : ""}
        </div>
        {fullList.length > 12 && (
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="名前で絞る"
            style={{
              width: "100%",
              padding: "5px 8px",
              marginBottom: 6,
              fontSize: 13,
              background: "var(--bg-field)",
              color: "var(--text)",
              border: "1px solid var(--line)",
              borderRadius: 6,
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxHeight: fullList.length > 12 ? 260 : undefined,
            overflowY: fullList.length > 12 ? "auto" : undefined,
          }}
        >
          {currentList.map((p) => (
            <div key={String(p.value)} style={{ display: "flex", gap: 4 }}>
              <button
                style={{
                  ...btn(!eraser && brushValue === p.value),
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                }}
                onClick={() => {
                  setBrushValue(p.value);
                  setEraser(false);
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    background: p.color,
                    border: "1px solid rgba(0,0,0,0.5)",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.label}
                </span>
              </button>
              {field === "state_ref" && (
                <button
                  style={{ ...btn(docSlug === p.value), width: "auto", padding: "4px 8px" }}
                  onClick={() => openDoc(String(p.value))}
                  title="この州の説明を開く"
                >
                  文
                </button>
              )}
            </div>
          ))}
          <button style={btn(eraser)} onClick={() => setEraser(true)}>
            消しゴム
          </button>
        </div>

        <div style={label}>筆の太さ {brushSize}</div>
        <input
          type="range"
          min={1}
          max={30}
          value={brushSize}
          onChange={(e) => setBrushSize(Number(e.target.value))}
          style={{ width: "100%" }}
        />
          </>
        )}

        <div style={label}>表示</div>
        <button style={btn(showLine)} onClick={() => setShowLine((v) => !v)}>
          境界線 {showLine ? "あり" : "なし"}
        </button>
        <button
          style={{ ...btn(showStateLine), marginTop: 6 }}
          onClick={() => setShowStateLine((v) => !v)}
        >
          州の境界線 {showStateLine ? "あり" : "なし"}
        </button>
        <button
          style={{ ...btn(showTimezoneLine), marginTop: 6 }}
          onClick={() => setShowTimezoneLine((v) => !v)}
        >
          時間帯の境界線 {showTimezoneLine ? "あり" : "なし"}
        </button>
        {features.toggles}

        <div style={label}>拡大 {(view.zoom * 100).toFixed(0)}%</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button style={{ ...btn(false), textAlign: "center" }} onClick={() => zoomCenter(1 / 1.3)}>
            −
          </button>
          <button style={{ ...btn(false), textAlign: "center" }} onClick={fitWhole}>
            全体
          </button>
          <button style={{ ...btn(false), textAlign: "center" }} onClick={() => zoomCenter(1.3)}>
            ＋
          </button>
        </div>

        {mode === "paint" && (
        <div style={{ marginTop: 18 }}>
          <button
            onClick={save}
            disabled={dirtyCount === 0}
            style={{
              ...btn(dirtyCount > 0),
              textAlign: "center",
              padding: "10px 8px",
              opacity: dirtyCount === 0 ? 0.45 : 1,
              cursor: dirtyCount === 0 ? "default" : "pointer",
            }}
          >
            保存{dirtyCount > 0 ? `（${dirtyCount.toLocaleString()}）` : ""}
          </button>
        </div>
        )}

        <div style={{ marginTop: 8 }}>
          <button onClick={reload} style={{ ...btn(false), textAlign: "center" }}>
            master から読み直す
          </button>
        </div>

        <p style={{ fontSize: 11, color: "var(--text-dim)", lineHeight: 1.8, marginTop: 14 }}>
          {mode === "paint" ? "左ドラッグで塗る" : "地物を押して選ぶ。左ドラッグで動かす"}
          <br />
          Shift＋ドラッグで動かす
          <br />
          ホイールでカーソルを軸に拡大
        </p>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 12px",
            borderBottom: "1px solid var(--line)",
            background: "var(--bg-panel)",
            fontSize: 12,
          }}
        >
          <span>{status}</span>
          <span style={{ color: "var(--text-dim)" }}>
            {hover
              ? `${hover.id}　${hover.land ? (hover.coastal ? "陸・海沿い" : "陸") : "海"}` +
                (hover.nation ? `　${hover.nation}` : "") +
                (hover.state ? ` / ${hover.state}` : "") +
                (hover.features ? `　［${hover.features}］` : "")
              : "—"}
          </span>
        </div>
        <div
          ref={viewRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
            background: "#08080a",
            touchAction: "none",
            cursor:
              mode === "feature"
                ? features.editing
                  ? "crosshair"
                  : "grab"
                : hover && !hover.land && (LAND_ONLY.includes(field) || !allowSea)
                  ? "not-allowed"
                  : "crosshair",
          }}
          onWheel={onWheel}
          onContextMenu={(e) => e.preventDefault()}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onPointerLeave={features.onPointerLeave}
        >
          <canvas ref={viewCanvasRef} style={layer} />
          <canvas ref={lineCanvasRef} style={{ ...layer, pointerEvents: "none" }} />
          <canvas
            ref={(el) => {
              featureCanvasRef.current = el;
              features.canvasRef.current = el;
            }}
            style={{ ...layer, pointerEvents: "none" }}
          />
          {features.docPanel}

          {docSlug && (
            <div
              // 地図側がポインタを掴むため、この中の操作は親へ渡さない
              onPointerDown={(e) => e.stopPropagation()}
              onPointerMove={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onWheel={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: "min(560px, 60%)",
                background: "var(--bg-panel)",
                borderLeft: "1px solid var(--line)",
                display: "flex",
                flexDirection: "column",
                zIndex: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--line)",
                }}
              >
                <strong style={{ color: "var(--accent)", fontSize: 13 }}>
                  {docSlug} の説明
                </strong>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ ...btn(true), width: "auto", padding: "4px 12px" }} onClick={saveDoc}>
                    保存
                  </button>
                  <button
                    style={{ ...btn(false), width: "auto", padding: "4px 12px" }}
                    onClick={() => {
                      setDocSlug(null);
                      setDocStatus("");
                    }}
                  >
                    閉じる
                  </button>
                </div>
              </div>
              <textarea
                value={docText}
                onChange={(e) => setDocText(e.target.value)}
                spellCheck={false}
                style={{
                  flex: 1,
                  minHeight: 0,
                  resize: "none",
                  padding: "10px 12px",
                  background: "var(--bg)",
                  color: "var(--text)",
                  border: "none",
                  outline: "none",
                  fontFamily: "ui-monospace, Consolas, monospace",
                  fontSize: 13,
                  lineHeight: 1.7,
                }}
              />
              <div
                style={{
                  padding: "6px 12px",
                  borderTop: "1px solid var(--line)",
                  fontSize: 11,
                  color: "var(--text-dim)",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>1 行目の見出しを変えると州の名前も変わる</span>
                <span>{docStatus}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
